import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | authed | guest

    const bootstrap = useCallback(async () => {
        const token = tokenStore.get();
        if (!token) {
            setStatus("guest");
            return;
        }
        // Iter-140 — defensive bootstrap. Only TRULY clear the session
        // on a confirmed 401 from /auth/me. Network blips, 5xx, and
        // proxy timeouts during heavy upload activity must NOT silently
        // log the user out — the server-side session is still valid,
        // and clearing the token here was the root cause of the
        // "kicked to login mid-upload" stability bug during pressure
        // testing.
        const tryMe = async () => {
            try {
                const { data } = await api.get("/auth/me", { timeout: 15000 });
                setUser(data);
                setStatus("authed");
                return { ok: true };
            } catch (e) {
                const status = e?.response?.status;
                return { ok: false, status, err: e };
            }
        };
        let res = await tryMe();
        if (!res.ok && res.status !== 401) {
            // One quiet retry for transient transport-level issues
            // (network, 502/503/504, timeout).
            await new Promise((r) => setTimeout(r, 800));
            res = await tryMe();
        }
        if (res.ok) return;
        if (res.status === 401) {
            // Definitively expired/invalid token — clear and bounce.
            tokenStore.clear();
            setUser(null);
            setStatus("guest");
            return;
        }
        // Transient failure on both tries. Keep the token AND honour
        // the session optimistically with a minimal user shape so the
        // app keeps working. The next successful API call will refresh
        // the user record naturally.
        setUser({ id: null, email: null, name: null });
        setStatus("authed");
    }, []);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    // Iter-158.13.2.6 — Front Door auto-route gating. Set a one-shot
    // "just logged in" marker in sessionStorage on every successful
    // sign-in / registration / reset-mounted session. MyProjects
    // reads-and-clears this marker to decide whether to auto-jump a
    // single-workspace user into their sole dept. Without this
    // marker, manual visits to `/` (tapping the OnSetFlow wordmark,
    // browser back, etc.) NEVER auto-jump — so users can always
    // return to My Projects and see every project they are entitled
    // to.
    const _markJustLoggedIn = () => {
        try {
            sessionStorage.setItem("osf_just_logged_in", "1");
        } catch { /* private-mode fallback: auto-jump simply won't fire */ }
    };

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            tokenStore.set(data.token);
            setUser(data.user);
            setStatus("authed");
            _markJustLoggedIn();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const register = async (name, email, password) => {
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            tokenStore.set(data.token);
            setUser(data.user);
            setStatus("authed");
            _markJustLoggedIn();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = () => {
        tokenStore.clear();
        setUser(null);
        setStatus("guest");
    };

    /** Mounts an existing {token, user} pair as the active session.
     * Used by ResetPasswordPage which receives both from /auth/reset-password
     * so the user lands logged-in after a successful reset. */
    const applyAuth = (token, userObj) => {
        tokenStore.set(token);
        setUser(userObj);
        setStatus("authed");
    };

    return (
        <AuthContext.Provider value={{ user, status, login, register, logout, applyAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
